import React from 'react';
import styled from 'styled-components';
import { Input, NotificationBannerEnums, ButtonEnums } from 'components/atoms';
import { useSelector } from 'react-redux';
import { StrictDropdownProps, Dropdown, DropdownProps, DropdownItemProps, Icon } from 'semantic-ui-react';
import { Routes, EmploymentFormErrorMessage } from 'constants/index';
import { MembershipTypes, User } from 'mxp-schemas';
import { Admin as AdminUtils, CheckoutCountriesListHash } from 'mxp-utils';
import { isCenterMembershipSelector, userMembershipTypeSelector } from 'modules/membership/selectors';
import { getPath } from 'utils';
import { Link, LinkEnums } from 'components/atoms/Link/Link';
import { EmploymentPhoneInput } from 'components/atoms/Input/EmploymentPhoneInput';
import { getPageNameAndSiteSection } from 'utils/Analytics/helpers';
import {
  StyledAdvancedSearch,
  StyledAdvancedSearchLink,
  StyledAnyEdits,
  StyledCenterOrgTitle,
  StyledChangeRoleSpan,
  StyledCheckBox,
  StyledCloseButton,
  StyledCMHeading,
  StyledContainerPhoneNumber,
  StyledDescription,
  StyledDropdown,
  StyledDropdownMenuItem,
  StyledForm,
  StyledIconError,
  StyledInputWithValidation,
  StyledLabel,
  StyledLabelBusinessCity,
  StyledLabelBusinessName,
  StyledLink,
  StyledMessage,
  StyledNotificationBanner,
  StyledResetLink,
  StyledRoleContainer,
  StyledSearchButton,
  StyledSearchOrganizationContainer,
  StyledSearchOrganizationFormContainer,
  StyledVerticalLine,
  ValidationMessage,
} from './ApplicationEmployment.style';
import AddressForm, { Address, AddressType } from './AddressForm';

interface DropdownOption {
  key: number;
  text: string;
  value: string;
  sfOrgId?: string;
}

const optionsOrganizationType = Object.values(MembershipTypes.OrganizationType).map((value: any, index) => {
  return { key: index, value, text: value };
});

const optionsCountryList = Object.values(CheckoutCountriesListHash).map((country: any, index) => {
  return { key: index, value: country.ISOAlpha3Code, text: country.text, image: country.image };
});

interface Props {
  employerAlreadyExists: boolean;
  selectedMembershipType: string;
  searchOrganizationsByWildcardLoading: boolean;
  searchOrganizationsCitiesLoading: boolean;
  isAddressDetailsEditable: boolean;
  handleEditChange: () => void;
  employmentForm: State.NewAccountPersonAccount;
  handleCreateNewClick: () => Promise<void>;
  handleItemClick: (event: React.SyntheticEvent, data: DropdownItemProps) => void;
  handlePhoneNumberOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSetShippingAddressChange: (event: React.SyntheticEvent<Element, Event>, { checked }: any) => void;
  handleAddressLineChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    field: keyof Address,
    addressType: AddressType
  ) => void;
  handleAddressCountryChange: (
    event: React.SyntheticEvent<Element, Event>,
    data: StrictDropdownProps,
    addressType: AddressType
  ) => void;
  handleOrganizationTypeOnChange: (event: React.SyntheticEvent, data: StrictDropdownProps) => void;
  handleNewBusinessChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchBusinessCity: () => void;
  handleSearchCityChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchAgainBusinessCity: () => void;
  handleSearchAgainBusinessName: () => void;
  handleBusinessCityChange: (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => void;
  handleBusinessChange: (event: React.ChangeEvent<HTMLInputElement>, data: DropdownProps) => void;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>, { searchQuery }: { searchQuery: string }) => void;
  searchFn: (event: React.ChangeEvent<HTMLInputElement>, searchQuery: string) => DropdownOption[];
  handleSearchSFBusinessNameWithZipCode: () => void;
  handleSearchZIBusinessNameWithZipCode: () => void;
  phoneNumber: string;
  businessPostalCode?: string;
  businessState?: string;
  businessCity?: string;
  businessCountry?: string;
  addressLine2ErrorMessage: string;
  isAddressLine2IsValid: boolean;
  isBusinessNameValid: boolean;
  businessAddressLine2?: string;
  businessAddressLine1?: string;
  address: State.Address;
  shippingAddress: State.Address;
  businessType?: string;
  businessName?: string;
  role?: string;
  refDropdownBusinessName: any;
  refNewBusiness: any;
  businessCityElementId: string;
  businessNameElementId: string;
  isShippingAddressSameAsBusiness: boolean;
  existingEmployerFromSearch: boolean;
  hasPromoCode: boolean;
  stateBusinessList: { value: null; searchQuery: string; filteredList: DropdownOption[] };
  businessCities: DropdownOption[];
  isBusinessNew: boolean;
  isCenterMembershipJourney?: boolean;
  isForModal: boolean;
  isPaidByFirm?: boolean;
  hasExistingEmployers?: boolean;
  canEditEmploymentInfo?: boolean;
  handleEditPrimaryEmployerClick: any;
  onChangeRole: (event: React.SyntheticEvent, data: StrictDropdownProps) => void;
  onStatusChange: (event: React.SyntheticEvent, data: StrictDropdownProps) => void;
  tlwData: State.TLWInfoList;
  onTLWChange: (event: React.SyntheticEvent, data: StrictDropdownProps) => void;
  renderTLWReasonSelection: boolean;
  renderRoleSelection: boolean;
  renderEmploymentDetails: boolean;
  isSearchingExternal?: boolean;

  employers: Omit<State.AccountPersonAccount, 'personAccount'>[];
  employerSelectionUseCase: User.EmployerSelectionUseCaseEnum;
  handleUpdateEmployerSelectionUseCase: (useCase: User.EmployerSelectionUseCaseEnum) => void;
  handleSelectItemConnectedEmployer: (employer: Omit<State.AccountPersonAccount, 'personAccount'> | undefined) => void;
  handleSelectItemSearchedEmployer: (name: string, sfOrgId?: string) => void;
  handleSearchNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchZipCodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchTempStorage: {
    businessName: string;
    businessCity: string;
    businessZipCode: string;
  };
  handleBackToSearchConnectedOrganization: () => void;
  handleBackToSearchOrganizationSFZoomInfo: () => void;
  handleCenterPhoneNumberOnChange?: (dialCode: string, workPhoneNumber: string) => void;
  addressFormErrors: {
    line1?: boolean;
    city?: boolean;
    state?: boolean;
    zipCode?: boolean;
  };
  shippingAddressFormErrors: {
    line1?: boolean;
    city?: boolean;
    state?: boolean;
    zipCode?: boolean;
  };
  onCloseHandler: (value: any) => void;
  isClose: {
    businessName?: boolean;
    role?: boolean;
    workPhoneNumber?: boolean;
  };
}

export const EmploymentForm: React.FC<Props> = ({
  employerAlreadyExists,
  selectedMembershipType,
  searchOrganizationsByWildcardLoading,
  searchOrganizationsCitiesLoading,
  isAddressDetailsEditable,
  handleEditChange,
  employmentForm,
  handleCreateNewClick,
  handleItemClick,
  handlePhoneNumberOnChange,
  handleSetShippingAddressChange,
  handleAddressCountryChange,
  handleAddressLineChange,
  handleOrganizationTypeOnChange,
  handleNewBusinessChange,
  handleSearchBusinessCity,
  handleSearchCityChange,
  handleSearchAgainBusinessCity,
  handleSearchAgainBusinessName,
  handleBusinessCityChange,
  handleBusinessChange,
  handleSearchChange,
  searchFn,
  phoneNumber,
  handleSearchSFBusinessNameWithZipCode,
  handleSearchZIBusinessNameWithZipCode,
  canEditEmploymentInfo,
  businessPostalCode,
  businessState,
  businessCity,
  businessCountry,
  addressLine2ErrorMessage,
  isAddressLine2IsValid,
  isBusinessNameValid,
  businessAddressLine2,
  businessAddressLine1,
  address,
  shippingAddress,
  businessType,
  businessName,
  role,
  refDropdownBusinessName,
  refNewBusiness,
  businessCityElementId,
  businessNameElementId,
  isShippingAddressSameAsBusiness,
  existingEmployerFromSearch,
  hasPromoCode,
  stateBusinessList,
  businessCities,
  isBusinessNew,
  isCenterMembershipJourney,
  isForModal,
  isPaidByFirm,
  hasExistingEmployers,
  handleEditPrimaryEmployerClick,
  onChangeRole,
  onStatusChange,
  tlwData,
  onTLWChange,
  renderTLWReasonSelection,
  renderRoleSelection,
  renderEmploymentDetails,
  isSearchingExternal,
  employers,
  employerSelectionUseCase,
  handleUpdateEmployerSelectionUseCase,
  handleSelectItemConnectedEmployer,
  handleSelectItemSearchedEmployer,
  handleSearchNameChange,
  handleSearchZipCodeChange,
  searchTempStorage,
  handleBackToSearchConnectedOrganization,
  handleBackToSearchOrganizationSFZoomInfo,
  handleCenterPhoneNumberOnChange,
  addressFormErrors,
  shippingAddressFormErrors,
  onCloseHandler,
  isClose,
}) => {
  const userMembershipTypeChoice = useSelector(userMembershipTypeSelector) as string;
  const isCenterMemberJourney = useSelector(isCenterMembershipSelector);
  const employmentStatusOptions = MembershipTypes.EmploymentStatus.slice(0, 2);
  const { pageNameNoHash } = getPageNameAndSiteSection();

  const showCMBanner = isCenterMembershipJourney
    ? Boolean(
        AdminUtils.allInvalidOrgs(businessType) ||
          (AdminUtils.isPCPSType(userMembershipTypeChoice) && AdminUtils.pcpsInvalidOrgs(businessType)) ||
          (AdminUtils.similarGroupValidOrgs(userMembershipTypeChoice) &&
            (AdminUtils.pcpsInvalidOrgs(businessType) ||
              businessType === MembershipTypes.OrganizationType.CONSULTING_FIRM)) ||
          (AdminUtils.isCPEAType(userMembershipTypeChoice) && AdminUtils.cpeaInvalidOrgs(businessType))
        )
    : false;

  return (
    <StyledForm isForModal={isForModal}>
      {isForModal ? (
        <>
          <StyledLabel>
            Employment Status<span style={{ color: 'red' }}>*</span>
          </StyledLabel>
          <StyledDropdown
            selection
            value={employmentForm.status}
            options={employmentStatusOptions}
            onChange={onStatusChange}
            placeholder="Select an option"
          />

          {renderTLWReasonSelection && (
            <>
              <br />
              <StyledLabel>Please quickly explain why you are currently TLW</StyledLabel>
              <StyledDropdown
                selection
                value={tlwData.tlwReason}
                options={MembershipTypes.TLWReason}
                onChange={onTLWChange}
                placeholder="Select"
              />
            </>
          )}

          {/* USER SEARCHES ORGANIZATION FROM CONNECTED EMPLOYERS */}
          {employerSelectionUseCase === User.EmployerSelectionUseCaseEnum.SEARCH_TO_CONNECTED_EMPLOYERS &&
            renderRoleSelection && (
              <>
                {employers?.length > 0 && (
                  <>
                    <br />
                    <StyledLabel>
                      Employer<span style={{ color: 'red' }}>*</span>
                    </StyledLabel>
                    <StyledDropdown
                      searchInput={{ id: businessNameElementId }}
                      placeholder="Select Employer"
                      value={
                        employmentForm.organization.id &&
                        `${employmentForm.organization.name} - ${employmentForm.jobTitle}`
                      }
                      text={
                        employmentForm.organization.id &&
                        `${employmentForm.organization.name} - ${employmentForm.jobTitle}`
                      }
                      className="selection"
                    >
                      <Dropdown.Menu key="business-menu">
                        {employers.map(employer => {
                          return (
                            <Dropdown.Item
                              key={employer.id}
                              text={`${employer.organization.name} - ${employer.jobTitle}`}
                              value={employer.id}
                              // tslint:disable-next-line jsx-no-lambda
                              onClick={() => handleSelectItemConnectedEmployer(employer)}
                            />
                          );
                        })}
                      </Dropdown.Menu>
                    </StyledDropdown>
                  </>
                )}
                <StyledResetLink
                  // tslint:disable-next-line jsx-no-lambda
                  onClick={() =>
                    handleUpdateEmployerSelectionUseCase(
                      User.EmployerSelectionUseCaseEnum.SEARCH_TO_SALESFORCE_ZOOMINFO
                    )
                  }
                >
                  Search or add existing employer
                </StyledResetLink>
              </>
            )}

          {/* USER SEARCHES ORGANIZATION FROM SALESFORCE OR ZOOM INFO */}
          {employerSelectionUseCase === User.EmployerSelectionUseCaseEnum.SEARCH_TO_SALESFORCE_ZOOMINFO &&
            renderRoleSelection && (
              <>
                <br />
                {!canEditEmploymentInfo && (
                  <StyledResetLink onClick={handleBackToSearchConnectedOrganization}>Go Back</StyledResetLink>
                )}
                <StyledLabel>Search Organization:</StyledLabel>
                <StyledSearchOrganizationContainer>
                  <StyledSearchOrganizationFormContainer>
                    <StyledLabel>
                      Business Name<span style={{ color: 'red' }}>*</span>
                    </StyledLabel>
                    <StyledInputWithValidation
                      className="business-name"
                      name="Business-input"
                      placeholder="Business Name"
                      testId="search-business-name"
                      isCorrect={isBusinessNameValid}
                      type="text"
                      value={searchTempStorage.businessName}
                      validationMessageDisabled={isClose.businessName}
                      errorMessage={
                        <StyledMessage>
                          {EmploymentFormErrorMessage.BUSINESS_NAME_ERROR_MESSAGE}
                          <StyledCloseButton
                            testId="onclose-error-message"
                            className="error-message"
                            onClick={() => onCloseHandler('businessName')} // tslint:disable-line jsx-no-lambda
                          >
                            <span>&times;</span>
                          </StyledCloseButton>
                        </StyledMessage>
                      }
                      onChange={handleSearchNameChange}
                    />
                  </StyledSearchOrganizationFormContainer>
                  <StyledSearchOrganizationFormContainer>
                    <StyledLabel>Business Zip Code</StyledLabel>
                    <StyledInput
                      className="business-zipcode"
                      placeholder="Business Zip Code"
                      testId="search-business-zipcode"
                      value={searchTempStorage.businessZipCode}
                      onChange={handleSearchZipCodeChange}
                    />
                  </StyledSearchOrganizationFormContainer>
                  <StyledSearchButton
                    testId="search-business-button"
                    variant={ButtonEnums.variants.secondary}
                    iconPosition={ButtonEnums.iconPosition.left}
                    iconName="search"
                    others={{ pageName: `${pageNameNoHash}:employment` }}
                    onClick={handleSearchSFBusinessNameWithZipCode}
                  >
                    Search
                  </StyledSearchButton>
                </StyledSearchOrganizationContainer>

                {/* HIDE'S THE RESULT IF A SEARCHED ITEM IS CLICKED */}
                {!canEditEmploymentInfo && (
                  <>
                    <StyledAdvancedSearch>
                      if you don't see your organization, please use&nbsp;
                      <StyledAdvancedSearchLink onClick={handleSearchZIBusinessNameWithZipCode}>
                        Advanced Search
                      </StyledAdvancedSearchLink>
                    </StyledAdvancedSearch>
                    <br />
                    {/* SEARCH RESULTS FROM SF AND ZOOM INFO */}
                    Search results:
                    <ul>
                      {stateBusinessList.filteredList.map(option => {
                        return (
                          <li key={option.key}>
                            <StyledLink
                              // tslint:disable-next-line jsx-no-lambda
                              onClick={() => handleSelectItemSearchedEmployer(option.text, option.sfOrgId)}
                            >
                              {option.text}
                            </StyledLink>
                          </li>
                        );
                      })}
                      {/* SHOW THE ADD NEW LINK AFTER SEARCH IN ZOOM INFO */}
                      {isSearchingExternal && (
                        <li>
                          <StyledLink onClick={handleCreateNewClick}>Add new</StyledLink>
                        </li>
                      )}
                    </ul>
                  </>
                )}
              </>
            )}

          {/* EMPLOYER DETAILS SHOW'S IF THE USER CLICKED AN ITEM FROM SEARCH OR CLICKED CREATE MANUALLY */}
          {/* DISABLE'S THE ORGANIZATION RELATED FIELDS IF THE CLICKED SEARCH IS COMING FROM SALESFORCE */}
          {/* ENABLE'S THE ORGANIZATION RELATED FIELDS IF THE CLICKED SEARCH IS COMING FROM ZOOM INFO */}
          {canEditEmploymentInfo && !renderTLWReasonSelection && (
            <>
              <br />
              <StyledResetLink onClick={handleBackToSearchOrganizationSFZoomInfo}>Go Back</StyledResetLink>
              {(employmentForm.organization.name || isBusinessNew) && (
                <>
                  <StyledVerticalLine>
                    <StyledLabel>
                      Your role <span style={{ color: 'red' }}>*</span>
                    </StyledLabel>
                    <StyledDropdown
                      selection
                      name="role-selection"
                      value={employmentForm.jobTitle}
                      options={MembershipTypes.Roles}
                      onChange={onChangeRole}
                      placeholder="Select from list"
                    />
                    {!employmentForm.jobTitle && (
                      <ValidationMessage hasError={true} absoluteErrorMessage={false} hidden={isClose.role}>
                        {EmploymentFormErrorMessage.ROLE_ERROR_MESSAGE}
                        <StyledCloseButton
                          testId="onclose-error-message"
                          className="error-message"
                          onClick={() => onCloseHandler('role')} // tslint:disable-line jsx-no-lambda
                        >
                          <span>&times;</span>
                        </StyledCloseButton>
                      </ValidationMessage>
                    )}
                    <br />

                    <StyledLabel>
                      Name <span style={{ color: 'red' }}>*</span>
                    </StyledLabel>
                    <StyledInput
                      inputRef={refNewBusiness}
                      placeholder="New Business Names"
                      testId="new-business-name"
                      value={employmentForm.organization.name}
                      onChange={handleNewBusinessChange}
                      disabled={!isAddressDetailsEditable && !isBusinessNew}
                    />
                    <br />

                    <StyledLabel>
                      Organization Type <span style={{ color: 'red' }}>*</span>
                    </StyledLabel>
                    <StyledDropdown
                      selection
                      value={employmentForm?.organization?.type}
                      options={optionsOrganizationType?.filter(
                        orgType => orgType?.value !== MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING
                      )}
                      onChange={handleOrganizationTypeOnChange}
                      disabled={!isBusinessNew}
                    />
                  </StyledVerticalLine>
                  <StyledLabelBusinessCity>Business City</StyledLabelBusinessCity>
                  <StyledDropdown
                    searchInput={{ autoFocus: !isBusinessNew, id: businessCityElementId }}
                    placeholder="Search Business City"
                    options={businessCities}
                    search
                    onChange={handleBusinessCityChange}
                    selectOnNavigation
                    selectOnBlur={false}
                    selection
                    onSearchChange={handleSearchCityChange}
                    loading={!!searchOrganizationsCitiesLoading}
                    icon={{ className: 'search', onClick: () => handleSearchBusinessCity() }}
                    disabled={Boolean(businessCity)}
                    value={businessCity}
                    text={!isBusinessNew ? businessCity : ''}
                  />
                  {((employmentForm?.organization?.name && employmentForm?.organization?.id) ||
                    isBusinessNew ||
                    hasPromoCode) && (
                    <>
                      {!isBusinessNew && !hasPromoCode && (
                        <StyledResetLink onClick={handleSearchAgainBusinessCity}>Search again</StyledResetLink>
                      )}
                      {/* This is the AICPA membership signup journey
                      This form is used to display existing employer as well as to add a new employer */}
                      <StyledVerticalLine>
                        <AddressForm
                          addressType={AddressType.BILLING}
                          addressLine1={businessAddressLine1 ?? ''}
                          addressLine2={businessAddressLine2 ?? ''}
                          city={businessCity ?? ''}
                          state={businessState ?? ''}
                          postalCode={businessPostalCode ?? ''}
                          country={businessCountry ?? ''}
                          handleAddressLineChange={handleAddressLineChange}
                          handleAddressCountryChange={handleAddressCountryChange}
                          countryOptions={optionsCountryList}
                          disabled={!isAddressDetailsEditable && !isBusinessNew}
                          showEditButton={!isBusinessNew && !hasPromoCode}
                          handleEditChange={handleEditChange}
                          addressFormErrors={{ ...addressFormErrors, line2: !isAddressLine2IsValid }}
                          addressLine2ErrorMessage={addressLine2ErrorMessage}
                        />

                        <StyledCheckBox
                          height="18"
                          width="18"
                          id="checkbox"
                          testId="setShippingAddress"
                          label={isBusinessNew ? 'Set as shipping address' : 'Make this my primary contact address'}
                          checked={isShippingAddressSameAsBusiness}
                          onClick={handleSetShippingAddressChange}
                          disabled={!isAddressDetailsEditable && !isBusinessNew}
                        />

                        {!isShippingAddressSameAsBusiness && (
                          <AddressForm
                            addressType={AddressType.SHIPPING}
                            addressLine1={shippingAddress.addressLine1 ?? ''}
                            addressLine2={shippingAddress.addressLine2 ?? ''}
                            city={shippingAddress.city ?? ''}
                            state={shippingAddress.state ?? ''}
                            postalCode={shippingAddress.zipCode ?? ''}
                            country={shippingAddress.country ?? ''}
                            handleAddressLineChange={handleAddressLineChange}
                            handleAddressCountryChange={handleAddressCountryChange}
                            countryOptions={optionsCountryList}
                            disabled={!isAddressDetailsEditable && !isBusinessNew}
                            showEditButton={!isBusinessNew && !hasPromoCode}
                            addressFormErrors={shippingAddressFormErrors}
                          />
                        )}
                        <StyledContainerPhoneNumber>
                          <StyledLabel>
                            Work Phone Number <span style={{ color: 'red' }}>*</span>
                          </StyledLabel>
                          {isCenterMembershipJourney && handleCenterPhoneNumberOnChange ? (
                            <EmploymentPhoneInput
                              handleCenterPhoneNumberOnChange={handleCenterPhoneNumberOnChange}
                              placeholder="Work phone no."
                              disabled={isSearchingExternal ? false : !isAddressDetailsEditable && !isBusinessNew}
                            />
                          ) : (
                            <>
                              <StyledInputWithValidation
                                placeholder="Work phone no."
                                testId="workphonenumber"
                                name="workphonenumber"
                                isCorrect={phoneNumber.length}
                                type="text"
                                value={phoneNumber}
                                onChange={handlePhoneNumberOnChange}
                                disabled={isSearchingExternal ? false : !isAddressDetailsEditable && !isBusinessNew}
                                validationMessageDisabled={isClose.workPhoneNumber}
                                errorMessage={
                                  <ValidationMessage hasError={true} absoluteErrorMessage={false}>
                                    {EmploymentFormErrorMessage.PHONE_ERROR_MESSAGE}
                                    <StyledCloseButton
                                      testId="onclose-error-message"
                                      className="error-message"
                                      onClick={() => onCloseHandler('workPhoneNumber')} // tslint:disable-line jsx-no-lambda
                                    >
                                      <span>&times;</span>
                                    </StyledCloseButton>
                                  </ValidationMessage>
                                }
                              />
                            </>
                          )}
                        </StyledContainerPhoneNumber>
                        <StyledAnyEdits>
                          *Any edits made to the company address will only be saved to your profile. To make changes to
                          the master company address, contact our customer service team here.
                        </StyledAnyEdits>
                      </StyledVerticalLine>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      ) : (
        (!hasExistingEmployers || isCenterMembershipJourney) && (
          <>
            {!isCenterMembershipJourney && (
              <>
                {role && selectedMembershipType === User.MembershipTypes.REGULAR && (
                  <StyledRoleContainer>
                    <StyledLabel>Your role</StyledLabel>
                    <StyledInput testId="role" value={role} disabled />
                    <StyledChangeRoleSpan>
                      If you{' '}
                      <Link underline={LinkEnums.underline.noUnderlineOnHover} to={getPath(Routes.MEMBERSHIP_FORM)}>
                        change your role
                      </Link>
                      , you will have to make your tiers and products selection again.
                    </StyledChangeRoleSpan>
                  </StyledRoleContainer>
                )}
              </>
            )}
            {!hasPromoCode && (
              <>
                {isCenterMembershipJourney && (
                  <>
                    <StyledCMHeading>Search or create a new employer record</StyledCMHeading>
                    <StyledDescription>
                      When you start entering a business name, suggestions appear. If you see the correct business,
                      click “confirm.”
                    </StyledDescription>
                    <StyledDescription>
                      If the firm/organization does not appear, click the “Create new” button.
                    </StyledDescription>
                    <StyledDescription>
                      If you create a new record, you will be able to search for the firm/organization type.
                    </StyledDescription>
                    <br />
                  </>
                )}
                {(!isBusinessNew && isCenterMembershipJourney) || !hasExistingEmployers ? (
                  <>
                    {!(
                      (businessName || isBusinessNew) &&
                      !searchOrganizationsByWildcardLoading &&
                      !employerAlreadyExists
                    ) && (
                      <>
                        <StyledLabelBusinessName>Business name</StyledLabelBusinessName>
                        <StyledDropdown
                          // onBlur={clearSearch}
                          ref={refDropdownBusinessName}
                          searchInput={{ id: businessNameElementId }}
                          placeholder="Search Business Name"
                          search={searchFn}
                          onChange={handleBusinessChange}
                          selectOnNavigation
                          selectOnBlur={false}
                          onSearchChange={handleSearchChange}
                          loading={!!searchOrganizationsByWildcardLoading}
                          icon={<Icon name="search" onClick={handleSearchSFBusinessNameWithZipCode} />}
                          disabled={
                            (businessName || isBusinessNew) &&
                            !searchOrganizationsByWildcardLoading &&
                            !employerAlreadyExists
                          }
                          value={businessName}
                          text={!isBusinessNew ? businessName : ''}
                          className="selection"
                        >
                          <Dropdown.Menu key="business-menu">
                            {stateBusinessList.filteredList.map(option => {
                              return (
                                <Dropdown.Item
                                  key={option.key}
                                  text={option.text}
                                  value={option.value}
                                  onClick={handleItemClick}
                                />
                              );
                            })}
                            <StyledDropdownMenuItem text={'+ Create New'} onClick={handleCreateNewClick} />
                          </Dropdown.Menu>
                        </StyledDropdown>
                      </>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
            {(businessName || isBusinessNew) &&
              !searchOrganizationsByWildcardLoading &&
              (!employerAlreadyExists || existingEmployerFromSearch) && (
                <>
                  <StyledLabel>{isCenterMembershipJourney ? 'Business name' : 'Name'}</StyledLabel>
                  <StyledInput
                    inputRef={refNewBusiness}
                    placeholder="New Business Name"
                    testId="new-business-name"
                    value={businessName}
                    onChange={handleNewBusinessChange}
                    disabled={!isAddressDetailsEditable && !isBusinessNew}
                  />

                  {!isPaidByFirm && (
                    <StyledResetLink onClick={handleSearchAgainBusinessName} hidden={hasPromoCode}>
                      Search again
                    </StyledResetLink>
                  )}
                  <StyledVerticalLine>
                    {!isCenterMembershipJourney && (
                      <>
                        <StyledLabel>Your role</StyledLabel>
                        <StyledDropdown
                          selection
                          name="role-selection"
                          value={employmentForm.jobTitle}
                          options={MembershipTypes.Roles}
                          onChange={onChangeRole}
                          placeholder="Select from list"
                        />
                        {!employmentForm.jobTitle && (
                          <ValidationMessage hasError={true} absoluteErrorMessage={false}>
                            Please select a role from the dropdown menu in order to continue
                          </ValidationMessage>
                        )}
                        <br />
                      </>
                    )}

                    <StyledLabel>Organization type</StyledLabel>
                    <StyledDropdown
                      selection
                      value={businessType}
                      placeholder="Select firm/organization type"
                      options={optionsOrganizationType?.filter(orgType => {
                        const type = orgType?.value;
                        if (AdminUtils.isEBPAQCType(userMembershipTypeChoice) || AdminUtils.isCAQType(selectedMembershipType)) {
                          return type === MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING;
                        }
                        if (AdminUtils.isGAQCType(userMembershipTypeChoice)) {
                          return type === MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING ||
                            type === MembershipTypes.OrganizationType.GOVERNMENT;
                        }
                        if (AdminUtils.isPCPSType(userMembershipTypeChoice)) {
                          return type === MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING ||
                            type === MembershipTypes.OrganizationType.CONSULTING_FIRM;
                        }
                        if (AdminUtils.isCAQType(userMembershipTypeChoice)) {
                          return type === MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING
                        }
                        if (AdminUtils.isCPEAType(userMembershipTypeChoice)) {
                            return type === MembershipTypes.OrganizationType.BUSINESS ||
                                   type === MembershipTypes.OrganizationType.EDUCATION || 
                                   type === MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING ||
                                   type === MembershipTypes.OrganizationType.CONSULTING_FIRM;
                        }
                        return isCenterMemberJourney
                          ? Boolean(orgType)
                          : type !== MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING;
                      })}
                      onChange={handleOrganizationTypeOnChange}
                      disabled={!isBusinessNew}
                    />
                    <>
                      {(() => {
                        if (isCenterMembershipJourney) {
                          if (showCMBanner) {
                            return (
                              <StyledNotificationBanner
                                testId="organization-type-error"
                                childrenTestId="membership-application-employment-error-children"
                                icon={<StyledIconError />}
                                variant={NotificationBannerEnums.variant.blue}
                              >
                                This firm/organization type is not eligible for the center membership chosen. Please
                                contact the center team with any questions.
                              </StyledNotificationBanner>
                            );
                          }
                        }
                      })()}
                    </>
                    {businessType === MembershipTypes.OrganizationType.PUBLIC_ACCOUNTING && (
                      <StyledNotificationBanner
                        testId="organization-type-error"
                        childrenTestId="membership-application-employment-error-children"
                        icon={<StyledIconError />}
                        variant={NotificationBannerEnums.variant.blue}
                      >
                        {/* Creating a new public accounting firm requires additional information about your firm's
                        practice. */}
                        Firms subject to Peer Review must complete further attestation.
                      </StyledNotificationBanner>
                    )}
                  </StyledVerticalLine>
                  {!hasPromoCode && !showCMBanner && !isCenterMembershipJourney && (
                    <>
                      <StyledLabelBusinessCity>Company's location</StyledLabelBusinessCity>
                      <StyledDropdown
                        searchInput={{ autoFocus: !isBusinessNew, id: businessCityElementId }}
                        placeholder="Search Business City"
                        options={businessCities}
                        search
                        onChange={handleBusinessCityChange}
                        selectOnNavigation
                        selectOnBlur={false}
                        selection
                        onSearchChange={handleSearchCityChange}
                        loading={!!searchOrganizationsCitiesLoading}
                        icon={{ className: 'search', onClick: () => handleSearchBusinessCity() }}
                        disabled={Boolean(businessCity) || isBusinessNew}
                        value={businessCity}
                        text={!isBusinessNew ? businessCity : ''}
                      />
                    </>
                  )}
                  {(isPaidByFirm ||
                    (businessName && employmentForm?.organization?.id) ||
                    isBusinessNew ||
                    isSearchingExternal ||
                    hasPromoCode) &&
                    !showCMBanner && (
                      <>
                        {!isBusinessNew && !hasPromoCode && !isCenterMembershipJourney && (
                          <StyledResetLink onClick={handleSearchAgainBusinessCity}>Search again</StyledResetLink>
                        )}
                        <StyledVerticalLine>
                          {isCenterMembershipJourney && !isBusinessNew ? (
                            // This is to display existing firm address in center membership journey
                            <>
                              <StyledCenterOrgTitle>Organization Address</StyledCenterOrgTitle>
                              <AddressForm
                                disabled={true}
                                addressLine1={employmentForm.organization.billingAddress.addressLine1 ?? ''}
                                addressLine2={employmentForm.organization.billingAddress.addressLine2 ?? ''}
                                city={employmentForm.organization.billingAddress.city ?? ''}
                                state={employmentForm.organization.billingAddress.state ?? ''}
                                country={employmentForm.organization.billingAddress.country ?? ''}
                                postalCode={employmentForm.organization.billingAddress.zipCode ?? ''}
                                addressType={AddressType.BILLING}
                              />

                              <StyledContainerPhoneNumber>
                                <StyledLabel>Phone Number</StyledLabel>
                                <StyledInput
                                  placeholder="Work phone no."
                                  testId="workphonenumber"
                                  value={employmentForm.organization.phone}
                                  onChange={handlePhoneNumberOnChange}
                                  disabled={!isAddressDetailsEditable && !isBusinessNew}
                                />
                              </StyledContainerPhoneNumber>
                            </>
                          ) : (
                            // This is to create a new firm address in center membership journey
                            <>
                              <AddressForm
                                handleAddressLineChange={handleAddressLineChange}
                                handleAddressCountryChange={handleAddressCountryChange}
                                disabled={!isSearchingExternal && !isAddressDetailsEditable && !isBusinessNew}
                                addressLine1={businessAddressLine1 ?? ''}
                                addressLine2={businessAddressLine2 ?? ''}
                                city={businessCity ?? ''}
                                state={businessState ?? ''}
                                postalCode={businessPostalCode ?? ''}
                                country={businessCountry ?? ''}
                                addressType={AddressType.BILLING}
                                countryOptions={optionsCountryList}
                                addressFormErrors={{ ...addressFormErrors, line2: !isAddressLine2IsValid }}
                                addressLine2ErrorMessage={addressLine2ErrorMessage}
                              />

                              <StyledCheckBox
                                height="18"
                                width="18"
                                id="checkbox"
                                testId="setShippingAddress"
                                label="Set as shipping address"
                                checked={isShippingAddressSameAsBusiness}
                                onClick={handleSetShippingAddressChange}
                                disabled={isSearchingExternal ? false : !isAddressDetailsEditable && !isBusinessNew}
                              />
                              {!isShippingAddressSameAsBusiness && (
                                <AddressForm
                                  handleAddressLineChange={handleAddressLineChange}
                                  handleAddressCountryChange={handleAddressCountryChange}
                                  disabled={!isSearchingExternal && !isAddressDetailsEditable && !isBusinessNew}
                                  addressLine1={shippingAddress?.addressLine1 ?? ''}
                                  addressLine2={shippingAddress?.addressLine2 ?? ''}
                                  city={shippingAddress?.city ?? ''}
                                  state={shippingAddress?.state ?? ''}
                                  postalCode={shippingAddress?.zipCode ?? ''}
                                  country={shippingAddress?.country ?? ''}
                                  addressType={AddressType.SHIPPING}
                                  addressFormErrors={shippingAddressFormErrors}
                                  countryOptions={optionsCountryList}
                                />
                              )}
                              <StyledContainerPhoneNumber>
                                <StyledLabel>Work Phone Number</StyledLabel>

                                {isCenterMembershipJourney && handleCenterPhoneNumberOnChange ? (
                                  <EmploymentPhoneInput
                                    handleCenterPhoneNumberOnChange={handleCenterPhoneNumberOnChange}
                                    placeholder="Work phone no."
                                    disabled={isSearchingExternal ? false : !isAddressDetailsEditable && !isBusinessNew}
                                  />
                                ) : (
                                  <StyledInput
                                    placeholder="Work phone no."
                                    testId="workphonenumber"
                                    value={phoneNumber}
                                    onChange={handlePhoneNumberOnChange}
                                    disabled={isSearchingExternal ? false : !isAddressDetailsEditable && !isBusinessNew}
                                  />
                                )}
                              </StyledContainerPhoneNumber>
                              <StyledAnyEdits>
                                *Any edits made to the company address will only be saved to your profile. To make
                                changes to the master company address, contact us by selecting the chat icon to the
                                right of your screen or calling 888-777-7077. The Global Engagement Center is open
                                9:00am–8:00pm ET Monday through Thursday, and 9:00am–6:00pm ET on Friday.
                              </StyledAnyEdits>
                            </>
                          )}
                        </StyledVerticalLine>
                      </>
                    )}
                </>
              )}
          </>
        )
      )}
    </StyledForm>
  );
};

const StyledInput = styled(({ hasError, ...rest }) => <Input {...rest} />)`
  &&& {
    display: flex;
    margin-bottom: ${props => props.theme.pxToRem(7)};
  }

  &&&.disabled {
    cursor: not-allowed;
    opacity: 1;
    input {
      border-color: ${props => props.theme.colors.neutralGrey4};
      background-color: ${props => props.theme.colors.neutralGrey2};
    }
  }

  ${({ hasError, theme }) =>
    hasError &&
    `
      &&&& > input {
        :focus {
          border: solid ${theme.pxToRem(1)} ${theme.colors.interfaceRed};
        }
         border: solid ${theme.pxToRem(1)} ${theme.colors.interfaceRed};
      }
    `}
`;
